import { useState } from 'react'
import {
  Gift, RefreshCw, Download, CheckCircle2, ChevronDown, Users,
} from 'lucide-react'
import {
  getAdminReferrals, adminReferralMarkPaid,
} from '../../services/api'
import {
  C, Card, Btn, Pill, Loading, ErrorLine, Empty,
  useAsync, confirmAsync, fmtMoney, fmtInt, fmtDate, fmtDateTime,
} from './adminUI'

// Раздел «Рефералы» в админ-панели (п.4.4 ПЛАН_МАРКЕТИНГА): кто сколько
// накопил и что пора выплатить. Начисление — 10% от ПЕРВОЙ оплаченной
// подписки «друга» (сервер, один раз на друга). Владелец переводит вручную
// до 5-го числа месяца за прошлый месяц и отмечает «выплачено» здесь.
//
// overview.dueDate/prevMonth/curMonth — из бэкенда (реферальный модуль db.js);
// «сегодня» считается по UTC+3, как и везде в статистике бота.

// Ровно одна строка на «друга»: начисление за его первую подписку.
function buildCsv(rows) {
  const esc = v => {
    const s = v == null ? '' : String(v)
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const head = ['referrer', 'friend', 'plan', 'amount_usd', 'reward_usd', 'period', 'status', 'paid_at', 'note', 'created_at']
  const lines = [head.join(';')]
  for (const g of rows) {
    for (const f of g.friends) {
      lines.push([
        esc(g.name), esc(f.name), esc(f.planCode), f.amountUsd, f.rewardUsd,
        esc(f.period), esc(f.status), esc(f.paidAt), esc(f.note), esc(f.createdAt),
      ].join(';'))
    }
  }
  return lines.join('\n')
}

export default function ReferralSection() {
  const [exporting, setExporting] = useState(false)
  const [paying, setPaying] = useState(false)
  const [openRef, setOpenRef] = useState(null)
  const [note, setNote] = useState('')

  const { data, loading, error, reload } = useAsync(() => getAdminReferrals(), [])

  const ov = data?.overview || {}
  const rows = data?.rows || []
  const pendingFriends = rows.reduce((n, g) => n + g.friends.filter(f => f.status === 'pending').length, 0)

  const refresh = () => { setPaying(false); reload() }

  // «Выплачено за {prevMonth}» — все начисления прошлого месяца (к ним дедлайн
  // 5-го текущего). Отметка по месяцу: надёжно и не зависит от id строк.
  const markAllPrev = async () => {
    if (paying || !ov.pendingPrev) return
    const ok = await confirmAsync(
      `Отметить выплаченными ${ov.prevPendingCount || pendingFriends} начислений ` +
      `за ${ov.prevMonth} ($${fmtMoney(ov.pendingPrev)})?\n\n` +
      'Владелец перевёл деньги — строки станут «выплачено» с датой и пометкой.')
    if (!ok) return
    setPaying(true)
    try {
      const r = await adminReferralMarkPaid({ period: ov.prevMonth, note: note.trim() || null })
      if (!r.ok) throw new Error(r.error || 'Ошибка')
      refresh()
    } catch (e) {
      alert(`Не удалось отметить: ${e?.message || e}`)
    }
  }

  // Отметить ОДНО начисление (если владелец платил выборочно).
  const markOne = async (f) => {
    if (paying) return
    const ok = await confirmAsync(
      `Отметить выплаченным начисление $${fmtMoney(f.rewardUsd)} ` +
      `(друг ${f.name}, ${f.period})?`)
    if (!ok) return
    setPaying(true)
    try {
      const r = await adminReferralMarkPaid({ ids: [f.id], note: note.trim() || null })
      if (!r.ok) throw new Error(r.error || 'Ошибка')
      refresh()
    } catch (e) {
      alert(`Не удалось отметить: ${e?.message || e}`)
    }
  }

  const doExport = () => {
    if (exporting || !rows.length) return
    setExporting(true)
    try {
      const csv = buildCsv(rows)
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `referral_earnings_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('[Referral] Export failed:', e)
      alert(`Не удалось выгрузить CSV: ${e?.message || e}`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <ErrorLine onRetry={reload}>{error}</ErrorLine>

      {/* ── Сводка ── */}
      <Card
        title="Сводка · 10% от первой подписки друга"
        icon={Gift}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn small ghost icon={RefreshCw} color={C.muted} onClick={refresh}>Обновить</Btn>
            <Btn small icon={Download} disabled={exporting || !rows.length} onClick={doExport}>
              {exporting ? '…' : 'CSV'}
            </Btn>
          </div>
        }
      >
        {loading && !data ? <Loading /> : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div style={{ background: C.inner, borderRadius: 12, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 4 }}>К выплате 5-го</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: ov.pendingPrev > 0 ? C.warn : C.dim, fontFamily: 'monospace' }}>
                  ${fmtMoney(ov.pendingPrev)}
                </div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                  {ov.prevPendingCount || 0} начислений · {ov.prevMonth || ''} · до {fmtDate(ov.dueDate)}
                </div>
              </div>
              <div style={{ background: C.inner, borderRadius: 12, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 4 }}>Начислено всего</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                  ${fmtMoney(ov.accruedAll)}
                </div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                  {fmtInt(ov.rowsCount)} начислений · {fmtInt(ov.referrersCount)} рефереров
                </div>
              </div>
              <div style={{ background: C.inner, borderRadius: 12, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 4 }}>Выплачено</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>
                  ${fmtMoney(ov.paidAll)}
                </div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>всего за всё время</div>
              </div>
              <div style={{ background: C.inner, borderRadius: 12, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 4 }}>В этом месяце ({ov.curMonth || ''})</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                  ${fmtMoney(ov.accruingThisMonth)}
                </div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                  выплата — 5-го следующего
                </div>
              </div>
            </div>

            {/* Пометка для строк «выплачено» (записывается в note при отметке) */}
            <div style={{ marginBottom: ov.pendingPrev > 0 ? 10 : 0 }}>
              <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 6 }}>
                Пометка к выплате (необязательно)
              </div>
              <input
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="напр. «переведено на USDT (TRC20)»"
                style={{
                  width: '100%', padding: '9px 11px', boxSizing: 'border-box',
                  background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none',
                }}
              />
            </div>

            {ov.pendingPrev > 0 && (
              <div>
                <Btn
                  full icon={CheckCircle2}
                  disabled={paying}
                  onClick={markAllPrev}
                >
                  {paying ? 'Отмечаю…' :
                    `Отметить выплаченным за ${ov.prevMonth} — ${ov.prevPendingCount} шт ($${fmtMoney(ov.pendingPrev)})`}
                </Btn>
              </div>
            )}

            {ov.enabled === false && (
              <div style={{
                marginTop: 10, background: 'rgba(248,113,113,0.08)',
                border: '1px solid rgba(248,113,113,0.25)', borderRadius: 12,
                padding: '9px 12px', fontSize: 11.5, color: C.danger,
              }}>
                Начисления выключены в admin_config.json (referrals.enabled = false) — новые начисления не создаются.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Детализация: рефереры и их друзья ── */}
      <Card title="Рефереры и друзья" icon={Users}>
        {loading && !data ? <Loading /> : !rows.length ? (
          <Empty>Начислений пока нет — ссылки ещё никого не привели к первой подписке.</Empty>
        ) : (
          <div>
            {rows.map((g, i) => {
              const open = openRef === g.referrerId
              const pend = g.friends.filter(f => f.status === 'pending').length
              return (
                <div key={g.referrerId} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
                  <div
                    onClick={() => setOpenRef(open ? null : g.referrerId)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <ChevronDown
                      size={15}
                      color={C.dim}
                      style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
                        {g.name}
                        {g.refCode ? <span style={{ color: C.dim, fontWeight: 400, marginLeft: 6 }}>ref_{g.refCode}</span> : null}
                      </div>
                      <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                        пригласил {fmtInt(g.invitedCount)} · заплатили первые подписки {fmtInt(g.friendsCount)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                        ${fmtMoney(g.rewardUsd)}
                      </div>
                      <div style={{ fontSize: 10.5, color: C.dim, marginTop: 1 }}>
                        {pend > 0 ? <span style={{ color: C.warn }}>{pend} к выплате</span> : 'выплачено'}
                      </div>
                    </div>
                  </div>

                  {open && (
                    <div style={{
                      marginTop: 8, background: C.inner, borderRadius: 12, padding: '4px 12px 6px',
                    }}>
                      {g.friends.map(f => (
                        <div key={f.id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#fff' }}>
                                {f.name}
                                <span style={{ color: C.dim, fontWeight: 400, marginLeft: 6 }}>
                                  {f.planCode} · {f.period}
                                </span>
                              </div>
                              <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                                подписка ${fmtMoney(f.amountUsd)} → тебе {fmtMoney(f.rewardUsd)} · {fmtDateTime(f.createdAt)}
                              </div>
                              {f.status === 'paid' && (
                                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                                  выплачено {fmtDateTime(f.paidAt)}{f.note ? ` · ${f.note}` : ''}
                                </div>
                              )}
                            </div>
                            {f.status === 'pending' ? (
                              <>
                                <Pill color={C.warn}>ждёт</Pill>
                                <Btn
                                  small color={C.accent}
                                  disabled={paying}
                                  onClick={() => markOne(f)}
                                >
                                  Выплачено
                                </Btn>
                              </>
                            ) : (
                              <Pill color={C.accent}>выплачено</Pill>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
